import React from "react";
import {
  UploadSimple,
  FilePdf,
  FileImage,
  FileDoc,
  X,
  Warning,
  Plus,
  Sparkle,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";
import { Button } from "../../../components/ui/Button";
import { Label } from "../../../components/ui/Input";
import {
  MAX_FILES,
  fileKind,
  formatBytes,
} from "../shared";

function FileIcon({ kind }) {
  if (kind === "pdf")
    return <FilePdf size={18} weight="duotone" className="text-status-abgelehnt-text" />;
  if (kind === "docx")
    return <FileDoc size={18} weight="duotone" className="text-brand-700" />;
  return <FileImage size={18} weight="duotone" className="text-brand-500" />;
}

/**
 * The Step-3 file-upload pane: dropzone + file list + validation error
 * + action buttons.
 *
 * Iter 19.63.66 / Runde 10 — Action-Buttons umstrukturiert:
 *   • Extract-Button bleibt im Upload-Panel
 *   • Die drei Sekundaer-Buttons (manuelle Zeile, Recalc, Reset) sind
 *     nach Runde 10 aus dem Upload-Panel entfernt und in der Row-Region
 *     bzw. im Footer platziert (siehe StepThree.jsx).
 *
 * Purely presentational — all state lives in the parent StepThree.
 */
export default function FileUploadPane({
  inputRef,
  files,
  totalSize,
  error,
  extracting,
  reviewing,
  rowsCount,
  onFilePick,
  onDrop,
  onDragOver,
  onRemoveFile,
  onExtract,
  onAddManual,
  // Iter 19.63.66 — Neue Props (ersetzen onReview / onClearAll).
  onRecalcSum,
  onOpenReset,
  // When true, the dropzone is hidden and only a compact summary bar shows;
  // user can re-expand with the caret toggle. Parent controls the state so
  // it can auto-collapse after a successful KI-extraction (rowsCount > 0).
  collapsed,
  onToggleCollapsed,
  // Iter 19.43.7 — Dirty-State-Marker. Wenn true, ist seit dem letzten
  // KI-Check vom User editiert worden → Summe-Aktualisieren-Button wird auffällig.
  dirty = false,
}) {
  return (
    <div data-testid="tool-step3-upload">
      <div className="flex items-end justify-between mb-2 gap-3 flex-wrap">
        <div>
          <Label className="mb-0">Dokumente hochladen</Label>
          <div className="mt-1 text-[11px] text-warm-600">
            Empfohlen: <strong>Versicherungsdatenauszug (SV-Auszug), Dienstzeugnisse, Lebenslauf</strong>
          </div>
        </div>
        {files.length > 0 && (
          <span className="text-[11px] text-warm-500">
            {files.length} / {MAX_FILES} · {formatBytes(totalSize)}
          </span>
        )}
      </div>

      {collapsed ? (
        <button
          type="button"
          onClick={onToggleCollapsed}
          data-testid="tool-step3-upload-expand"
          className="w-full flex items-center justify-between gap-3 border border-ink-200 bg-warm-50 hover:bg-warm-100 rounded-sm px-4 py-2.5 text-sm text-ink-700 transition-crisp"
        >
          <span className="flex items-center gap-2">
            <UploadSimple size={15} weight="duotone" className="text-brand-500" />
            <span className="font-medium">
              {files.length} {files.length === 1 ? "Dokument" : "Dokumente"} hochgeladen
            </span>
            <span className="text-warm-500 text-[11px] tabular-nums">
              · {formatBytes(totalSize)}
            </span>
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-brand-700">
            <Plus size={11} weight="bold" />
            Weitere hinzufügen
            <CaretDown size={11} weight="bold" />
          </span>
        </button>
      ) : (
        <>
          <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            className="border-2 border-dashed border-ink-300 bg-warm-50 rounded-sm px-6 py-8 text-center transition-crisp hover:border-brand-400 hover:bg-brand-50"
            data-testid="tool-step3-dropzone"
          >
            <UploadSimple size={28} weight="duotone" className="text-brand-500 mx-auto mb-2" />
            <div className="text-sm text-ink-900 font-medium">
              Dateien hier ablegen oder{" "}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="text-brand-700 underline underline-offset-2 hover:no-underline font-semibold"
                data-testid="tool-step3-pick"
              >
                auswählen
              </button>
            </div>
            <div className="text-[11px] text-warm-600 mt-1">
              Max. {MAX_FILES} Dateien · je 15 MB · PDF, DOCX, JPG, PNG
            </div>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
              multiple
              onChange={onFilePick}
              className="sr-only"
              data-testid="tool-step3-file-input"
            />
          </div>
          {files.length > 0 && onToggleCollapsed && (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={onToggleCollapsed}
                data-testid="tool-step3-upload-collapse"
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-warm-600 hover:text-ink-900 transition-crisp"
              >
                <CaretUp size={11} weight="bold" />
                Upload-Bereich einklappen
              </button>
            </div>
          )}
        </>
      )}

      {files.length > 0 && !collapsed && (
        <ul
          className="mt-3 border border-ink-200 rounded-sm divide-y divide-ink-100 bg-white"
          data-testid="tool-step3-file-list"
        >
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="flex items-center gap-3 px-4 py-2.5">
              <FileIcon kind={fileKind(f)} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-ink-900 truncate">{f.name}</div>
                <div className="text-[11px] text-warm-500">{formatBytes(f.size)}</div>
              </div>
              <button
                type="button"
                onClick={() => onRemoveFile(i)}
                className="text-warm-400 hover:text-status-abgelehnt-text transition-crisp"
                data-testid={`tool-step3-file-remove-${i}`}
              >
                <X size={14} weight="bold" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Consent already collected globally in Step 1 — no opt-in here. */}

      {error && (
        <div
          role="alert"
          data-testid="tool-step3-error"
          className="mt-3 flex items-start gap-2 border border-status-abgelehnt-border bg-status-abgelehnt-bg px-3 py-2 rounded-sm text-sm text-status-abgelehnt-text"
        >
          <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="mt-4 flex gap-3 flex-wrap">
        <Button
          variant="brand"
          size="md"
          onClick={onExtract}
          disabled={extracting || files.length === 0}
          data-testid="tool-step3-ai-extract"
        >
          <Sparkle size={14} weight="bold" />
          {extracting ? "Dokumente werden ausgewertet…" : "Beschäftigungszeiten automatisch extrahieren"}
        </Button>
        {/* Iter 20.03.P5-fix Runde 10 — Die zusaetzlichen Buttons
            (manuelle Zeile, Rekalkulation, Reset) wurden aus dem
            Upload-Panel entfernt:
              • Manuelles Hinzufuegen erfolgt jetzt via Plus-Row am
                Ende der Vordienstzeit-Liste (siehe StepThree.jsx).
              • Auto-Recalc uebernimmt die Rekalkulation automatisch.
              • Reset sitzt jetzt im Footer links vom Next-Button. */}
      </div>
    </div>
  );
}
